import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import Utils from "../../utils/utils";
import { getAccessPermissions, getErrorMessage } from "../../common";
import { useSelector } from "react-redux";
import { RootState } from "../../globals";
import { AdvisorSelection, EnrollmentHistoryDTO } from "../../models/EnrollmentHistoryDTO";
import EnrollmentNotesTimeline from "../../components/enrollment/enrollmentNotesTimeline";
import AdvisorsPicker from "../../components/enrollment/advisorsPicker";
import { formatLocalDateTime } from "../../components/enrollment/format";
import { horarioBonito } from "../../utils/horarioBonito";
import EnrollStudentModal from "./enrollStudentModal";
import { ENROLLMENT_STATUS } from "../../constants";
import rotationService from "../../services/v2/rotation.service";
import RotationPicker from "../../components/enrollment/rotationPicker";
import { RotationDTO } from "../../models/RotationDTO";
import courseBlocksService from "../../services/v2/courseBlocks.service";
import EnrollmentBlocksCard from "../../components/enrollment/enrollmentBlocksCard";
import { EnrollmentBlocksDTO } from "../../models/CourseBlockDTO";
import { ScheduleChangeDTO, ScheduleChangeHistoryDTO } from "../../models/ScheduleChangeDTO";
import { fechaBonita } from "../../utils/fechaBonita";
import { sinAcentos } from "../../utils/cursoCorto";
import MiniModalController from "../../components/modals/miniModal/miniModalController";
import { EnrollmentDueDayDTO } from "../../models/EnrollmentDueDayDTO";

/**
 * Curso compuesto: the enrollment is of Hotelería but its current schedule belongs to Gastronomía.
 * The server tells us with `scheduleCourseName`; when it equals the enrollment's course nothing changes.
 */
const cursaEnOtroCurso = (enrollment: EnrollmentHistoryDTO): boolean => {
	const del = sinAcentos(enrollment.scheduleCourseName);
	return del !== "" && del !== sinAcentos(enrollment.courseName);
};

export interface StudentHistoryTabProps {
	studentId: number;
	studentName: string;
	studentIdentityCode?: string;
	campusId: number;
	canEnroll: boolean;
	/** Called after a successful enrollment so the parent list can refresh. */
	onEnrolled?: () => void;
}

/**
 * "Historial" tab of the student profile: every enrollment (any campus) with who enrolled them and when,
 * the advisors credited, the note history and an attendance summary. Also hosts the "Matricular" button.
 */
const StudentHistoryTab: React.FC<StudentHistoryTabProps> = ({ studentId, studentName, studentIdentityCode, campusId, canEnroll, onEnrolled }) => {
	const http = HttpManager.getInstance();
	const [history, setHistory] = useState<EnrollmentHistoryDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showEnroll, setShowEnroll] = useState<boolean>(false);
	// Matricular = solicitud nueva en Matrículas con la alumna ya elegida (Esteban, 2026-10-26); solo quien puede crear solicitudes.
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const puedeSolicitar: boolean = !!getAccessPermissions(currentUser, "enrollmentRequests")?.create;
	const [editingAdvisorsOf, setEditingAdvisorsOf] = useState<number | null>(null);
	//Acepta null porque el selector distingue "sin responder" de "Ninguno"; al guardar una
	//matrícula ya existente, sin responder equivale a dejarla sin asesoras.
	const [advisorsDraft, setAdvisorsDraft] = useState<AdvisorSelection[] | null>([]);
	const [savingAdvisors, setSavingAdvisors] = useState<boolean>(false);
	const [advisorsError, setAdvisorsError] = useState<string | null>(null);
	//Alumno rotativo: horarios autorizados por matrícula activa (se cargan aparte del historial).
	const [rotations, setRotations] = useState<{ [enrollmentId: number]: RotationDTO | null }>({});
	const [editingRotationOf, setEditingRotationOf] = useState<number | null>(null);
	const [rotationDraft, setRotationDraft] = useState<{ rotating: boolean; scheduleIds: number[] }>({ rotating: false, scheduleIds: [] });
	const [savingRotation, setSavingRotation] = useState<boolean>(false);
	const [rotationError, setRotationError] = useState<string | null>(null);

	const load = useCallback(() => {
		setError(null);
		http.enrollmentService.getStudentHistory(campusId, studentId)
			.then((response) => setHistory(response.data))
			.catch((e) => { setHistory([]); setError(getErrorMessage(e)); });
	}, [campusId, studentId]);

	useEffect(() => { load(); }, [load]);

	const puedeRotar = (enrollment: EnrollmentHistoryDTO): boolean =>
		enrollment.isCurrentCampus && enrollment.statusId === ENROLLMENT_STATUS.ACTIVE;

	useEffect(() => {
		if (!history) {
			return;
		}
		let cancelled = false;
		history.filter(puedeRotar).forEach((enrollment) => {
			rotationService.getRotation(campusId, studentId, enrollment.id)
				.then((response) => { if (!cancelled) { setRotations((previo) => ({ ...previo, [enrollment.id]: response.data })); } })
				.catch(() => { if (!cancelled) { setRotations((previo) => ({ ...previo, [enrollment.id]: null })); } });
		});
		return () => { cancelled = true; };
	}, [history, campusId, studentId]);

	/*
		Vencimiento por alumna (FASE 1 Matrícula por curso, 2026-09-30): el día del horario o el propio de la
		alumna. Quién puede cambiarlo lo dice el servidor (canEdit: Supervisión de Caja, Contraloría, superAdmin);
		sin permiso de lectura en la sección la línea no sale. null = no se pudo cargar.
	*/
	const [dueDays, setDueDays] = useState<{ [enrollmentId: number]: EnrollmentDueDayDTO | null }>({});
	const [dueDayOf, setDueDayOf] = useState<EnrollmentHistoryDTO | null>(null);
	const [dueDayDraft, setDueDayDraft] = useState<{ day: string; note: string }>({ day: "", note: "" });
	const [savingDueDay, setSavingDueDay] = useState<boolean>(false);
	const [dueDayError, setDueDayError] = useState<string | null>(null);
	const [dueDayDone, setDueDayDone] = useState<{ [enrollmentId: number]: string }>({});

	useEffect(() => {
		if (!history) {
			return;
		}
		let cancelled = false;
		history.filter(puedeRotar).forEach((enrollment) => {
			http.enrollmentUpdatesService.getDueDay(campusId, enrollment.id)
				.then((response) => { if (!cancelled) { setDueDays((previo) => ({ ...previo, [enrollment.id]: response.data })); } })
				.catch(() => { if (!cancelled) { setDueDays((previo) => ({ ...previo, [enrollment.id]: null })); } });
		});
		return () => { cancelled = true; };
	}, [history, campusId, studentId]);

	const startEditingDueDay = (enrollment: EnrollmentHistoryDTO) => {
		const actual = dueDays[enrollment.id];
		setDueDayError(null);
		setDueDayDraft({ day: actual && actual.effectiveDueDay ? String(actual.effectiveDueDay) : "", note: "" });
		setDueDayOf(enrollment);
	};

	const saveDueDay = async () => {
		if (!dueDayOf) {
			return;
		}
		const day = Number(dueDayDraft.day);
		const note = dueDayDraft.note.trim();
		if (!Number.isInteger(day) || day < 1 || day > 28 || note.length < 3) {
			setDueDayError(Constants.Common.reasonIsRequired);
			return;
		}
		setSavingDueDay(true);
		setDueDayError(null);
		try {
			const response = await http.enrollmentUpdatesService.setDueDay(campusId, { enrollmentId: dueDayOf.id, dueDay: day, note });
			setDueDays((previo) => ({ ...previo, [dueDayOf.id]: response.data }));
			//Cuántas mensualidades se movieron lo dice la nota de la matrícula que el servidor agrega al historial.
			setDueDayDone((previo) => ({ ...previo, [dueDayOf.id]: Constants.Common.dueDaySaved }));
			setDueDayOf(null);
			load();
		} catch (e) {
			setDueDayError(getErrorMessage(e));
		} finally {
			setSavingDueDay(false);
		}
	};

	//Curso compuesto: bloques por matrícula activa; null = la matrícula no es de un curso compuesto.
	const [blocks, setBlocks] = useState<{ [enrollmentId: number]: EnrollmentBlocksDTO | null }>({});

	useEffect(() => {
		if (!history) {
			return;
		}
		let cancelled = false;
		history.filter(puedeRotar).forEach((enrollment) => {
			courseBlocksService.getEnrollmentBlocks(campusId, studentId, enrollment.id)
				.then((response) => { if (!cancelled) { setBlocks((previo) => ({ ...previo, [enrollment.id]: response.data?.isComposite ? response.data : null })); } })
				.catch(() => { if (!cancelled) { setBlocks((previo) => ({ ...previo, [enrollment.id]: null })); } });
		});
		return () => { cancelled = true; };
	}, [history, campusId, studentId]);

	//Cambio de horario: horario actual, opciones e historial por matrícula activa. null = no se pudo cargar.
	const [scheduleChanges, setScheduleChanges] = useState<{ [enrollmentId: number]: ScheduleChangeDTO | null }>({});
	const [editingScheduleOf, setEditingScheduleOf] = useState<number | null>(null);
	const [scheduleDraft, setScheduleDraft] = useState<{ scheduleId: number | null; reason: string }>({ scheduleId: null, reason: "" });
	const [savingSchedule, setSavingSchedule] = useState<boolean>(false);
	const [scheduleError, setScheduleError] = useState<string | null>(null);
	const [scheduleDone, setScheduleDone] = useState<{ [enrollmentId: number]: string }>({});

	useEffect(() => {
		if (!history) {
			return;
		}
		let cancelled = false;
		history.filter(puedeRotar).forEach((enrollment) => {
			rotationService.getScheduleChange(campusId, studentId, enrollment.id)
				.then((response) => { if (!cancelled) { setScheduleChanges((previo) => ({ ...previo, [enrollment.id]: response.data })); } })
				.catch(() => { if (!cancelled) { setScheduleChanges((previo) => ({ ...previo, [enrollment.id]: null })); } });
		});
		return () => { cancelled = true; };
	}, [history, campusId, studentId]);

	const startEditingSchedule = (enrollment: EnrollmentHistoryDTO) => {
		setScheduleError(null);
		setScheduleDraft({ scheduleId: null, reason: "" });
		setScheduleDone((previo) => { const copia = { ...previo }; delete copia[enrollment.id]; return copia; });
		setEditingScheduleOf(enrollment.id);
		if (scheduleChanges[enrollment.id] === undefined) {
			return;
		}
		//Se vuelve a pedir al abrir: el cupo de los horarios cambia mientras la ficha está abierta.
		rotationService.getScheduleChange(campusId, studentId, enrollment.id)
			.then((response) => setScheduleChanges((previo) => ({ ...previo, [enrollment.id]: response.data })))
			.catch((e) => setScheduleError(getErrorMessage(e)));
	};

	const saveSchedule = async (enrollment: EnrollmentHistoryDTO) => {
		if (scheduleDraft.scheduleId === null) {
			setScheduleError(Constants.Common.chooseNewSchedule);
			return;
		}
		setSavingSchedule(true);
		setScheduleError(null);
		try {
			const motivo = scheduleDraft.reason.trim();
			const response = await rotationService.changeSchedule(campusId, studentId, enrollment.id, {
				scheduleId: scheduleDraft.scheduleId,
				...(motivo ? { reason: motivo } : {})
			});
			const movidas = response.data?.history?.[0]?.movedTuitions ?? 0;
			setScheduleChanges((previo) => ({ ...previo, [enrollment.id]: response.data }));
			setScheduleDone((previo) => ({ ...previo, [enrollment.id]: Constants.Common.scheduleChanged.replace("{count}", String(movidas)) }));
			setEditingScheduleOf(null);
			//El encabezado de la tarjeta trae el nombre del horario: se recarga el historial completo.
			load();
		} catch (e) {
			setScheduleError(getErrorMessage(e));
		} finally {
			setSavingSchedule(false);
		}
	};

	/** "Domingo · 9:00 am – 1:00 pm · Barbería 1 · Agosto 2026 · Juan Pérez · 8/12" */
	const scheduleOptionLabel = (option: ScheduleChangeDTO["options"][number]): string =>
		[horarioBonito(option.name), option.levelName, option.periodName, option.mentorName || Constants.Common.noMentor, `${option.enrolled}/${option.limit}`]
			.filter((parte) => parte !== null && parte !== undefined && String(parte).trim() !== "")
			.join(" · ");

	const scheduleHistoryLine = (entry: ScheduleChangeHistoryDTO): string =>
		[fechaBonita(entry.changedAt), `${horarioBonito(entry.fromScheduleName)} → ${horarioBonito(entry.toScheduleName)}`, entry.reason, entry.changedBy]
			.filter((parte) => parte && String(parte).trim() !== "")
			.join(" · ");

	const renderScheduleHistory = (entries: ScheduleChangeHistoryDTO[]) => (
		<div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12, color: "#6B5F58" }}>
			{entries.map((entry, index) => (
				<div key={"sc_" + index}>{scheduleHistoryLine(entry)}</div>
			))}
		</div>
	);

	const rotationText = (enrollment: EnrollmentHistoryDTO): string => {
		const rotation = rotations[enrollment.id];
		if (rotation === undefined) {
			return Constants.Common.loading;
		}
		if (rotation === null) {
			return "-";
		}
		return rotation.rotating
			? Constants.Common.rotatingSchedules.replace("{count}", String(rotation.scheduleIds.length))
			: Constants.Common.fixedSchedule;
	};

	const startEditingRotation = (enrollment: EnrollmentHistoryDTO) => {
		const rotation = rotations[enrollment.id];
		setRotationError(null);
		setRotationDraft({ rotating: rotation?.rotating ?? false, scheduleIds: rotation?.scheduleIds ?? [] });
		setEditingRotationOf(enrollment.id);
	};

	const saveRotation = async (enrollment: EnrollmentHistoryDTO) => {
		setSavingRotation(true);
		setRotationError(null);
		try {
			const rotating = rotationDraft.rotating && rotationDraft.scheduleIds.length > 0;
			const response = await rotationService.updateRotation(campusId, studentId, enrollment.id, {
				rotating,
				scheduleIds: rotating ? rotationDraft.scheduleIds : []
			});
			setRotations((previo) => ({ ...previo, [enrollment.id]: response.data }));
			setEditingRotationOf(null);
		} catch (e) {
			setRotationError(getErrorMessage(e));
		} finally {
			setSavingRotation(false);
		}
	};

	const startEditingAdvisors = (enrollment: EnrollmentHistoryDTO) => {
		setAdvisorsError(null);
		setAdvisorsDraft(enrollment.advisors.map((advisor) => ({ userId: advisor.userId, name: advisor.name, sharePercentage: advisor.sharePercentage })));
		setEditingAdvisorsOf(enrollment.id);
	};

	const saveAdvisors = async (enrollment: EnrollmentHistoryDTO) => {
		setSavingAdvisors(true);
		setAdvisorsError(null);
		try {
			await http.enrollmentService.updateAdvisors(campusId, studentId, enrollment.id, advisorsDraft ?? []);
			setEditingAdvisorsOf(null);
			load();
		} catch (e) {
			setAdvisorsError(getErrorMessage(e));
		} finally {
			setSavingAdvisors(false);
		}
	};

	const attendanceText = (enrollment: EnrollmentHistoryDTO): string => {
		const attendance = enrollment.attendance;
		if (!attendance || attendance.total < 1) {
			return Constants.Common.noClassesYet;
		}
		return `${attendance.present}/${attendance.total} ${Constants.Common.classesRegistered} · ${attendance.rate}%`;
	};

	return (
		<div style={{ padding: "10px 5px", textAlign: "left" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
				<h6 style={{ margin: 0 }}>{Constants.Common.enrollments}</h6>
				{puedeSolicitar ? (
					<Link to={"/enrollmentRequests?matricular=" + encodeURIComponent(studentIdentityCode || "")} className="btn btn-primary rounded-pill">
						<FontAwesomeIcon icon={solid("user-plus")} />
						&nbsp;{Constants.Common.enroll}
					</Link>
				) : ""}
			</div>
			{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
			{history === null ? <div className="loader" /> : (history.length < 1 ? <div style={{ padding: 15 }}>-Sin registros-</div> : (
				history.map((enrollment) => (
					<div key={"h_" + enrollment.id} className="card" style={{ marginBottom: 10, borderLeft: `4px solid ${enrollment.isCurrentCampus ? "var(--main-color)" : "var(--wa-filo)"}` }}>
						<div className="card-body" style={{ padding: "10px 14px" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
								<div style={{ flex: 1, minWidth: 0 }}>
									{cursaEnOtroCurso(enrollment) ? (
										<>
											{/* Curso compuesto: la matrícula es del curso (Hotelería) y hoy cursa el bloque en otro horario. */}
											<b>{enrollment.courseName}</b>
											<div style={{ fontSize: 13 }}>
												<span style={{ color: "var(--wa-apagado)" }}>{Constants.Common.takingIn}:</span> {[
													enrollment.scheduleCourseName, enrollment.levelName, horarioBonito(enrollment.scheduleName, { separador: " ", compacto: true })
												].filter((parte) => parte && String(parte).trim() !== "").join(" · ")}
												{enrollment.scheduleCode ? ` [Horario ${enrollment.scheduleCode}]` : ""}
											</div>
										</>
									) : (
										<>
											<b>{enrollment.courseName}</b> · {enrollment.levelName}
											{/*
												"Domingo 4:00 – 7:30 pm [Horario SCVQGN33QGP]": el horario en am/pm y una sola vez.
												Antes salía el nombre crudo en 24 h y además las horas de sesión, que dicen lo mismo;
												y la fecha de término, que nadie consulta aquí (Esteban, 2026-09-23).
											*/}
											<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
												{horarioBonito(enrollment.scheduleName, { separador: " ", compacto: true })}
												{enrollment.scheduleCode ? ` [Horario ${enrollment.scheduleCode}]` : ""}
											</div>
										</>
									)}
									<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
										{enrollment.periodName} · {Constants.Common.startsOn} {formatLocalDateTime(enrollment.startDate)}
										{enrollment.mentorName ? <> · {Constants.Common.mentor}: {enrollment.mentorName}</> : ""}
									</div>
									{(enrollment.buildingName || enrollment.classroomName) ? (
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{[enrollment.buildingName, enrollment.classroomName].filter(Boolean).join(" · ")}
										</div>
									) : ""}
									<div style={{ fontSize: 13, marginTop: 2 }}>
										<span style={{ color: "var(--wa-apagado)" }}>{Constants.Common.enrollmentAttendance}:</span> {attendanceText(enrollment)}
									</div>
									{puedeRotar(enrollment) && dueDays[enrollment.id] && dueDays[enrollment.id]!.effectiveDueDay != null ? (
										<div style={{ fontSize: 13, marginTop: 2 }}>
											<span style={{ color: "var(--wa-apagado)" }}>{Constants.Common.dueDay}:</span> {dueDays[enrollment.id]!.effectiveDueDay}
											{dueDays[enrollment.id]!.overrideDueDay != null ? <span style={{ color: "var(--wa-apagado)" }}> ({Constants.Common.dueDayOwn})</span> : ""}
											{dueDays[enrollment.id]!.canEdit ? (
												<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12 }} disabled={savingDueDay}
													onClick={() => startEditingDueDay(enrollment)}>
													<FontAwesomeIcon icon={solid("calendar-day")} />&nbsp;{Constants.Common.changeDueDay}
												</button>
											) : ""}
											{dueDayDone[enrollment.id] ? <div style={{ fontSize: 13, color: "#1F7A54" }}>{dueDayDone[enrollment.id]}</div> : ""}
										</div>
									) : ""}
									{puedeRotar(enrollment) ? (
										<div style={{ fontSize: 13, marginTop: 2 }}>
											<span style={{ color: "var(--wa-apagado)" }}>{Constants.Common.authorizedSchedules}:</span> {rotationText(enrollment)}
											{/* Mismo criterio que el servidor: quien puede editar alumnos (Esteban, 2026-09-24). */}
											{(canEnroll && rotations[enrollment.id] && editingRotationOf !== enrollment.id) ? (
												<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12 }} title={Constants.Common.authorizedSchedules}
													onClick={() => startEditingRotation(enrollment)}>
													<FontAwesomeIcon icon={solid("pen")} />
												</button>
											) : ""}
										</div>
									) : ""}
									{editingRotationOf === enrollment.id ? (
										<div style={{ marginTop: 8 }}>
											<RotationPicker
												idPrefix={"rotation_" + enrollment.id}
												homeSchedule={rotations[enrollment.id]?.homeSchedule ?? null}
												options={rotations[enrollment.id]?.options ?? []}
												rotating={rotationDraft.rotating}
												scheduleIds={rotationDraft.scheduleIds}
												onChange={(rotating, scheduleIds) => setRotationDraft({ rotating, scheduleIds })}
												disabled={savingRotation}
											/>
											{rotationError ? <div className="text-danger" style={{ fontSize: 13, marginTop: 4 }}>{rotationError}</div> : ""}
											<div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
												<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={savingRotation} onClick={() => setEditingRotationOf(null)}>
													{Constants.Common.close}
												</button>
												<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={savingRotation} onClick={() => saveRotation(enrollment)}>
													{Constants.Common.saveChanges}
												</button>
											</div>
										</div>
									) : ""}
									{scheduleDone[enrollment.id] ? (
										<div style={{ fontSize: 13, color: "#1F7A54", marginTop: 6 }}>{scheduleDone[enrollment.id]}</div>
									) : ""}
									{editingScheduleOf === enrollment.id ? (
										<div style={{ marginTop: 8, background: "#FAF7F4", border: "1px solid #E9E1DA", borderRadius: 6, padding: "10px 14px" }}>
											<div style={{ fontWeight: 600, marginBottom: 4 }}>{Constants.Common.changeSchedule}</div>
											{scheduleChanges[enrollment.id] === undefined ? (
												<div style={{ fontSize: 13, color: "#6B5F58" }}>{Constants.Common.loading}</div>
											) : (scheduleChanges[enrollment.id] === null ? "" : (
												<>
													<div style={{ fontSize: 13, color: "#6B5F58", marginBottom: 6 }}>
														{Constants.Common.currentSchedule}: {[
															horarioBonito(scheduleChanges[enrollment.id]!.current.name),
															scheduleChanges[enrollment.id]!.current.levelName,
															scheduleChanges[enrollment.id]!.current.periodName
														].filter((parte) => parte && String(parte).trim() !== "").join(" · ")}
													</div>
													{scheduleChanges[enrollment.id]!.options.length < 1 ? (
														<div style={{ fontSize: 13, color: "#6B5F58" }}>{Constants.Common.noScheduleOptions}</div>
													) : (
														<>
															<select className="form-select form-select-sm" style={{ fontSize: 13, marginBottom: 6 }} disabled={savingSchedule}
																value={scheduleDraft.scheduleId ?? ""}
																onChange={(event) => setScheduleDraft((previo) => ({ ...previo, scheduleId: event.target.value === "" ? null : Number(event.target.value) }))}>
																<option value="">{Constants.Common.chooseNewSchedule}</option>
																{scheduleChanges[enrollment.id]!.options.map((option) => (
																	<option key={"sc_opt_" + option.id} value={option.id}>{scheduleOptionLabel(option)}</option>
																))}
															</select>
															<input type="text" className="form-control form-control-sm" style={{ fontSize: 13 }} maxLength={255} disabled={savingSchedule}
																placeholder={Constants.Common.reason} value={scheduleDraft.reason}
																onChange={(event) => setScheduleDraft((previo) => ({ ...previo, reason: event.target.value }))} />
														</>
													)}
												</>
											))}
											{scheduleError ? <div style={{ fontSize: 13, color: "#A8730A", marginTop: 4 }}>{scheduleError}</div> : ""}
											<div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
												<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={savingSchedule} onClick={() => setEditingScheduleOf(null)}>
													{Constants.Common.close}
												</button>
												<button type="button" className="btn btn-sm rounded-pill" style={{ background: "#F45030", borderColor: "#F45030", color: "#FFFFFF" }}
													disabled={savingSchedule || !scheduleChanges[enrollment.id] || scheduleDraft.scheduleId === null} onClick={() => saveSchedule(enrollment)}>
													{Constants.Common.changeScheduleAction}
												</button>
											</div>
											{(scheduleChanges[enrollment.id]?.history?.length ?? 0) > 0 ? (
												<div style={{ marginTop: 8 }}>
													<div style={{ fontSize: 12, fontWeight: 600, color: "#6B5F58", marginBottom: 2 }}>{Constants.Common.scheduleChanges}</div>
													{renderScheduleHistory(scheduleChanges[enrollment.id]!.history)}
												</div>
											) : ""}
										</div>
									) : ((scheduleChanges[enrollment.id]?.history?.length ?? 0) > 0 ? (
										<details style={{ marginTop: 6 }}>
											<summary style={{ fontSize: 12, color: "#6B5F58", cursor: "pointer" }}>
												{Constants.Common.scheduleChanges} ({scheduleChanges[enrollment.id]!.history.length})
											</summary>
											<div style={{ marginTop: 4 }}>{renderScheduleHistory(scheduleChanges[enrollment.id]!.history)}</div>
										</details>
									) : "")}
									{puedeRotar(enrollment) && blocks[enrollment.id] ? (
										<EnrollmentBlocksCard
											idPrefix={"blocks_" + enrollment.id}
											data={blocks[enrollment.id] as EnrollmentBlocksDTO}
											canEdit={canEnroll}
											onAssign={(blockId, scheduleId) => courseBlocksService.assignBlock(campusId, studentId, enrollment.id, blockId, scheduleId).then((response) => response.data)}
											onChanged={(data) => setBlocks((previo) => ({ ...previo, [enrollment.id]: data }))}
											onContinueParallel={() => courseBlocksService.continueParallel(campusId, studentId, enrollment.id).then((response) => response.data.blocks)}
											linkSiguienteBloque={`/enrollmentUpdates?alumno=${studentId}&matricula=${enrollment.id}`}
											onReload={load}
										/>
									) : ""}
								</div>
								<div style={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
									<Badge bg={Utils.renderStatusEnrollment(enrollment.statusId as any)}>{enrollment.status}</Badge>
									{!enrollment.isCurrentCampus ? <Badge bg="light" text="dark">{Constants.Common.otherCampus}</Badge> : ""}
								</div>
							</div>
							<hr style={{ margin: "8px 0" }} />
							{editingAdvisorsOf === enrollment.id ? (
								<div style={{ marginBottom: 8 }}>
									<AdvisorsPicker campusId={campusId} value={advisorsDraft} onChange={setAdvisorsDraft} disabled={savingAdvisors} />
									{advisorsError ? <div className="text-danger" style={{ fontSize: 13 }}>{advisorsError}</div> : ""}
									<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
										<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={savingAdvisors} onClick={() => setEditingAdvisorsOf(null)}>
											{Constants.Common.close}
										</button>
										<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={savingAdvisors} onClick={() => saveAdvisors(enrollment)}>
											{Constants.Common.saveChanges}
										</button>
									</div>
								</div>
							) : (
								<div>
									{enrollment.isCurrentCampus ? (
										<div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginBottom: 4 }}>
											{/* Activas, Aplazadas y Sin horario se actualizan desde el módulo Actualizaciones (FASE 2, 2026-10-01). */}
											{(canEnroll && [ENROLLMENT_STATUS.ACTIVE, 7, 9].includes(enrollment.statusId)) ? (
												<Link className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 12 }} to={`/enrollmentUpdates?alumno=${studentId}&matricula=${enrollment.id}`}>
													<FontAwesomeIcon icon={solid("arrows-rotate")} />&nbsp;{Constants.Common.enrollmentUpdates}
												</Link>
											) : ""}
											{canEnroll ? (
												<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 12 }} onClick={() => startEditingAdvisors(enrollment)}>
													<FontAwesomeIcon icon={solid("pen")} />&nbsp;{Constants.Common.editAdvisors}
												</button>
											) : ""}
										</div>
									) : ""}
									<EnrollmentNotesTimeline
										notes={enrollment.notes}
										createdAt={enrollment.createdAt}
										createdBy={enrollment.createdBy}
										advisors={enrollment.advisors}
										statusId={enrollment.statusId}
										status={enrollment.status}
										updatedAt={enrollment.updatedAt}
										updatedBy={enrollment.updatedBy}
									/>
								</div>
							)}
						</div>
					</div>
				))
			))}
			{showEnroll ? (
				<EnrollStudentModal
					studentId={studentId}
					studentName={studentName}
					campusId={campusId}
					onClose={() => setShowEnroll(false)}
					onEnrolled={() => { setShowEnroll(false); load(); if (onEnrolled) { onEnrolled(); } }}
				/>
			) : ""}
			<MiniModalController title={Constants.Common.changeDueDay} show={dueDayOf !== null} onClose={() => setDueDayOf(null)}>
				<div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "6px 2px", textAlign: "left" }}>
					<div style={{ fontWeight: 600 }}>{dueDayOf ? [dueDayOf.courseName, horarioBonito(dueDayOf.scheduleName, { separador: " ", compacto: true })].filter(Boolean).join(" · ") : ""}</div>
					{dueDayOf && dueDays[dueDayOf.id] ? (
						<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
							{Constants.Common.currentSchedule}: {Constants.Common.dueDay.toLowerCase()} {dueDays[dueDayOf.id]!.scheduleDueDay ?? "-"}
						</div>
					) : ""}
					<label style={{ position: "static", display: "block", fontSize: 13 }}>
						{Constants.Common.dueDay} (1–28)
						<input type="number" min={1} max={28} step={1} className="form-control form-control-sm" style={{ marginTop: 4, maxWidth: 140 }}
							value={dueDayDraft.day} disabled={savingDueDay}
							onChange={(event) => setDueDayDraft((previo) => ({ ...previo, day: event.target.value }))} />
					</label>
					<label style={{ position: "static", display: "block", fontSize: 13 }}>
						{Constants.Common.dueDayNote}
						<textarea className="form-control form-control-sm" rows={2} maxLength={1000} style={{ marginTop: 4 }} required
							value={dueDayDraft.note} disabled={savingDueDay}
							onChange={(event) => setDueDayDraft((previo) => ({ ...previo, note: event.target.value }))} />
					</label>
					{dueDayError ? <div style={{ fontSize: 13, color: "#A8730A" }}>{dueDayError}</div> : ""}
					<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
						<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={savingDueDay} onClick={() => setDueDayOf(null)}>
							{Constants.Common.close}
						</button>
						<button type="button" className="btn btn-sm rounded-pill" style={{ background: "var(--wa-acento)", borderColor: "var(--wa-acento)", color: "#FFFFFF" }}
							disabled={savingDueDay || dueDayDraft.day === "" || dueDayDraft.note.trim().length < 3} onClick={saveDueDay}>
							{Constants.Common.saveChanges}
						</button>
					</div>
					{dueDayOf && (dueDays[dueDayOf.id]?.history?.length ?? 0) > 0 ? (
						<div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12, color: "#6B5F58" }}>
							{dueDays[dueDayOf.id]!.history.map((entry) => (
								<div key={"dd_" + entry.id}>{[fechaBonita(entry.createdAt), `${Constants.Common.dueDay} ${entry.dueDay}`, entry.note, entry.createdBy].filter(Boolean).join(" · ")}</div>
							))}
						</div>
					) : ""}
				</div>
			</MiniModalController>
</div>
	);
};

export default StudentHistoryTab;
